import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Send, Bell, Mail, MessageCircle, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, pending: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'general',
    target_roles: [],
    target_users: [],
    send_via: ['in_app'],
    priority: 'normal'
  });

  const canCreateNotification = ['school_admin', 'teacher'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // All users should use my-notifications to see their own notifications
      // Super admin may want to see all, but for now use same endpoint
      const notificationsEndpoint = `${API_URL}/api/notifications/my-notifications`;
      
      const notificationsRes = await axios.get(notificationsEndpoint, { headers: getAuthHeader() });
      setNotifications(notificationsRes.data || []);
      
      const notifList = notificationsRes.data || [];
      const total = notifList.length;
      const unread = notifList.filter(n => !n.is_read).length;
      const read = notifList.filter(n => n.is_read).length;
      setStats({ total, sent: read, pending: unread });
      
      // Students and teachers only for admin users
      const isAdmin = ['school_admin', 'super_admin'].includes(user?.role);
      if (isAdmin && user.school_id) {
        try {
          const [studentsRes, teachersRes] = await Promise.all([
            axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }),
            axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() })
          ]);
          setStudents(studentsRes.data?.items || studentsRes.data || []);
          setTeachers(teachersRes.data || []);
        } catch (err) {
          console.log('Could not fetch students/teachers');
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load notifications');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/notifications`, formData, {
        headers: getAuthHeader()
      });
      
      toast.success('Notification sent successfully');
      setDialogOpen(false);
      setFormData({
        title: '',
        message: '',
        notification_type: 'general',
        target_roles: [],
        target_users: [],
        send_via: ['in_app'],
        priority: 'normal'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    }
  };

  const toggleRole = (role) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const toggleSendVia = (channel) => {
    setFormData(prev => ({
      ...prev,
      send_via: prev.send_via.includes(channel)
        ? prev.send_via.filter(c => c !== channel)
        : [...prev.send_via, channel]
    }));
  };

  const sentNotifications = notifications.filter(n => n.status === 'sent');
  const pendingNotifications = notifications.filter(n => n.status === 'pending');

  return (
    <DashboardLayout title="Notifications">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Notifications</CardTitle>
                <div className="bg-blue-500 p-2 rounded-lg">
                  <Bell className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Sent</CardTitle>
                <div className="bg-green-500 p-2 rounded-lg">
                  <Check className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending</CardTitle>
                <div className="bg-amber-500 p-2 rounded-lg">
                  <X className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Send Notification Button */}
        {canCreateNotification && (
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="send-notification-btn">
                  <Send className="h-4 w-4 mr-2" />
                  Send Notification
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Send New Notification</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Notification title"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Enter your message"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="type">Notification Type *</Label>
                    <Select
                      value={formData.notification_type}
                      onValueChange={(value) => setFormData({ ...formData, notification_type: value })}
                    >
                      <SelectTrigger id="type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="fee_reminder">Fee Reminder</SelectItem>
                        <SelectItem value="exam_alert">Exam Alert</SelectItem>
                        <SelectItem value="attendance_alert">Attendance Alert</SelectItem>
                        <SelectItem value="announcement">Announcement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority *</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(value) => setFormData({ ...formData, priority: value })}
                    >
                      <SelectTrigger id="priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Target Audience *</Label>
                    <div className="flex flex-wrap gap-2">
                      {['teacher', 'student', 'parent'].map(role => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => toggleRole(role)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            formData.target_roles.includes(role)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Select at least one role</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Send Via *</Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => toggleSendVia('in_app')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          formData.send_via.includes('in_app')
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        <Bell className="h-4 w-4" />
                        In-App
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSendVia('email')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          formData.send_via.includes('email')
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        <Mail className="h-4 w-4" />
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSendVia('sms')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          formData.send_via.includes('sms')
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        <MessageCircle className="h-4 w-4" />
                        SMS
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSendVia('whatsapp')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          formData.send_via.includes('whatsapp')
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Note: Email, SMS, WhatsApp require integration setup
                    </p>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={formData.target_roles.length === 0 || formData.send_via.length === 0}>
                      Send Notification
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Notifications Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all">All Notifications</TabsTrigger>
                <TabsTrigger value="sent">Sent</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-4">
                <NotificationList notifications={notifications} navigate={navigate} />
              </TabsContent>

              <TabsContent value="sent" className="mt-4">
                <NotificationList notifications={sentNotifications} navigate={navigate} />
              </TabsContent>

              <TabsContent value="pending" className="mt-4">
                <NotificationList notifications={pendingNotifications} navigate={navigate} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const NotificationList = ({ notifications, navigate }) => {
  if (notifications.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No notifications found</p>;
  }

  return (
    <div className="space-y-3">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors ${notification.link ? 'cursor-pointer' : ''}`}
          onClick={() => notification.link && navigate(notification.link)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">{notification.title}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  notification.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                  notification.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                  notification.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {notification.priority}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  notification.status === 'sent' ? 'bg-green-100 text-green-800' :
                  'bg-amber-100 text-amber-800'
                }`}>
                  {notification.status}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">{notification.message}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="capitalize">{(notification.notification_type || 'general').replace('_', ' ')}</span>
                <span>•</span>
                <span>To: {(notification.target_roles || []).join(', ') || 'All'}</span>
                <span>•</span>
                <span>Via: {(notification.send_via || ['in_app']).join(', ')}</span>
                <span>•</span>
                <span>{new Date(notification.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationsPage;